import { useEffect, useRef, useState } from "react";
import type { TreemapEntry, SentenceWithPost } from "./types";
import type { CardState } from "../../hooks/use-session-state";
import { posLabel } from "./pos-tag";
import { MorphologyDisplay } from "./morphology-display";
import { EtymologySlot } from "./etymology-slot";
import { SentenceRow } from "./sentence-row";
import { useKeyboardReview } from "../../hooks/use-keyboard-review";

type AnimState =
  | "entering"
  | "open"
  | "advance-known"
  | "advance-unknown"
  | "arriving-from-known"
  | "arriving-from-unknown"
  | "leaving";

interface Props {
  entry: TreemapEntry;
  rank: number;
  state: CardState;
  consecutiveKnowns: number;
  sentences: SentenceWithPost[] | null;
  loadingSentences: boolean;
  emptyQueue: boolean;
  onReview: (mark: "known" | "unknown") => void;
  onDismiss: () => void;
}

export function LiftedCard({
  entry,
  rank,
  state,
  consecutiveKnowns,
  sentences,
  loadingSentences,
  emptyQueue,
  onReview,
  onDismiss,
}: Props) {
  const [anim, setAnim] = useState<AnimState>("entering");
  const [flash, setFlash] = useState<"known" | "unknown" | null>(null);
  const prevEntryKey = useRef<string>(
    `${entry.jmdict_seq}::${entry.kana}::${entry.kanji}`,
  );
  const lockoutUntil = useRef(0);
  // Direction of the outgoing slide, captured at handleMark time so the
  // entry-change effect can resolve it without depending on `anim`.
  // Putting `anim` in the deps caused the effect to re-run on the
  // arriving-from-* setState, tearing down its own 340ms timeout and
  // leaving the second card stuck in arriving state (keyboard disabled).
  const outgoingDir = useRef<"known" | "unknown" | null>(null);

  // Detect entry change → arriving from a slide direction.
  useEffect(() => {
    const key = `${entry.jmdict_seq}::${entry.kana}::${entry.kanji}`;
    if (prevEntryKey.current !== key) {
      const dir = outgoingDir.current;
      const incoming: AnimState =
        dir === "known"
          ? "arriving-from-known"
          : dir === "unknown"
            ? "arriving-from-unknown"
            : "entering";
      outgoingDir.current = null;
      // setState in an effect on purpose: timed entry/exit animation — setAnim(incoming), then "open" via setTimeout.
      setAnim(incoming);
      const t = setTimeout(() => setAnim("open"), 340);
      prevEntryKey.current = key;
      return () => clearTimeout(t);
    }
  }, [entry.jmdict_seq, entry.kana, entry.kanji]);

  // First-mount enter animation.
  useEffect(() => {
    const t = setTimeout(() => setAnim("open"), 360);
    return () => clearTimeout(t);
  }, []);

  // Cascade reveal: kana → gloss → sentences over 1500ms. Resets on
  // every entry change so each newly-arrived card runs the cascade.
  const [reveal, setReveal] = useState(0);
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- deliberately violates react-useeffect-discipline §1.3: on an entry change this commits one frame of the previous entry's reveal state before this fires and resets it. Follow-up: kanban #191.
    setReveal(0);
    const t1 = setTimeout(() => setReveal(1), 500);
    const t2 = setTimeout(() => setReveal(2), 1000);
    const t3 = setTimeout(() => setReveal(3), 1500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [entry.jmdict_seq, entry.kana, entry.kanji]);

  const fadeStyle = (step: number) => ({
    opacity: reveal >= step ? 1 : 0,
    transition: "opacity 240ms ease-out",
  });

  const handleMark = (mark: "known" | "unknown") => {
    if (Date.now() < lockoutUntil.current) return;
    lockoutUntil.current = Date.now() + 360;
    outgoingDir.current = mark;
    setFlash(mark);
    setTimeout(() => setFlash(null), 280);
    setTimeout(() => {
      setAnim(mark === "known" ? "advance-known" : "advance-unknown");
      onReview(mark);
    }, 200);
  };

  const handleDismiss = () => {
    setAnim("leaving");
    setTimeout(onDismiss, 220);
  };

  useKeyboardReview({
    enabled: anim === "open" || anim === "entering",
    onUnknown: () => handleMark("unknown"),
    onKnown: () => handleMark("known"),
    onDismiss: handleDismiss,
  });

  return (
    <div
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- <dialog> is hidden until show() and brings top-layer and focus behaviour this in-flow card does not have
      role="dialog"
      aria-label={`Review ${entry.display}`}
      className="n5-lifted relative z-10 flex flex-col"
      data-state={anim}
      data-flash={flash ?? undefined}
    >
      {/* Header band */}
      <header
        className="border-rule flex items-baseline justify-between gap-3 border-b px-4 pt-3 pb-2.5"
        style={{ background: "var(--paper-tint)" }}
      >
        <div className="flex min-w-0 items-baseline gap-3">
          <span
            className="shrink-0 text-2xl sm:text-3xl"
            style={{
              fontFamily:
                "var(--font-jp-learner), var(--font-jp-display), serif",
              fontWeight: 500,
              color: "var(--ink)",
              letterSpacing: "-0.01em",
            }}
          >
            <MorphologyDisplay
              display={entry.display}
              pos={entry.pos}
              reading={entry.kana}
              size="lg"
            />
          </span>
          {entry.kana && entry.kana !== entry.display ? (
            <span
              className="n5-landmark-reading shrink-0"
              style={{ fontSize: "13px", ...fadeStyle(1) }}
            >
              {entry.kana}
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="n5-marginalia">{posLabel(entry.pos)}</span>
          <span className="n5-marginalia">
            № {rank.toString().padStart(3, "0")}
          </span>
          <button
            type="button"
            onClick={handleDismiss}
            className="text-ink-soft hover:text-ink ml-1 text-base leading-none transition-colors"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      </header>

      {entry.gloss ? (
        <p
          className="border-rule/70 text-ink-soft border-b px-4 py-2 text-sm italic"
          style={{ fontFamily: "var(--font-display), serif", ...fadeStyle(2) }}
        >
          {entry.gloss}
        </p>
      ) : null}

      <EtymologySlot display={entry.display} variant="card" />

      {/* Sentence rows */}
      <section
        className="min-h-[140px] flex-1 overflow-y-auto px-3 py-1"
        style={fadeStyle(3)}
      >
        {loadingSentences ? (
          <div className="n5-marginalia py-6 text-center">
            loading sentences…
          </div>
        ) : sentences && sentences.length > 0 ? (
          sentences.map((s) => <SentenceRow key={s.sentence_id} sentence={s} />)
        ) : (
          <div className="n5-marginalia py-6 text-center">
            no bluesky attestations yet
          </div>
        )}
      </section>

      {emptyQueue ? (
        <div
          className="n5-marginalia border-rule/70 border-t px-4 py-2 text-center"
          style={{ background: "var(--paper-tint)" }}
        >
          queue clear · dismiss to see the map
        </div>
      ) : null}

      {/* Footer: review buttons + consecutive-known dots */}
      <footer
        className="border-rule flex items-center justify-between gap-3 border-t px-3 py-3"
        style={{ background: "var(--paper-tint)" }}
      >
        <button
          type="button"
          className="n5-btn"
          data-variant="dashed"
          onClick={() => handleMark("unknown")}
        >
          ← Unknown
        </button>
        <ConsecutiveDots
          count={consecutiveKnowns}
          graduated={state === "graduated"}
        />
        <button
          type="button"
          className="n5-btn"
          data-variant="known"
          onClick={() => handleMark("known")}
        >
          Known →
        </button>
      </footer>
    </div>
  );
}

function ConsecutiveDots({
  count,
  graduated,
}: {
  count: number;
  graduated: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="n5-marginalia">consec.</span>
      {[0, 1].map((i) => {
        const filled = i < count;
        return (
          <span
            key={i}
            className="inline-block"
            aria-hidden
            style={{
              width: 10,
              height: 10,
              border: "1px solid var(--ink-soft)",
              background: graduated
                ? "var(--claimed)"
                : filled
                  ? "var(--ink)"
                  : "transparent",
            }}
          />
        );
      })}
    </div>
  );
}
